import React, { useRef } from 'react';
import Barcode from 'react-barcode';
import './BlendSheet.css';

function BlendSheet({ blend, batch, onClose }) {
  const printRef = useRef();

  const handleDownloadPdf = async () => {
    try {
      const response = await fetch(`/api/blends/${blend.id}/pdf?batchId=${batch.id}`);

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `blend-${blend.blend_number}-${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF: ' + error.message);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const originalContent = document.body.innerHTML;

    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContent;

    // Re-attach React after print
    window.location.reload();
  };

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const blendDate = new Date(blend.blend_date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Group work orders by customer for better organization
  const groupedWorkOrders = {};
  if (blend.blend_work_orders) {
    blend.blend_work_orders.forEach(wo => {
      if (!groupedWorkOrders[wo.customer_name]) {
        groupedWorkOrders[wo.customer_name] = [];
      }
      groupedWorkOrders[wo.customer_name].push(wo);
    });
  }

  return (
    <div className="blend-sheet-modal">
      <div className="blend-sheet-container" style={{ page: 'blend-sheet' }}>
        <div className="blend-actions">
          <button onClick={handleDownloadPdf} className="pdf-button">
            Download PDF for Melter
          </button>
          <button onClick={handlePrint} className="print-button">
            Print Preview
          </button>
          <button onClick={onClose} className="close-button">
            Close
          </button>
        </div>

        <div ref={printRef} className="blend-sheet">
          <div className="blend-page">
            {/* Header */}
            <div className="blend-header">
              <h1 className="company-name">Production Blend Sheet</h1>
              <div className="blend-number-large">
                {blend.blend_number}
              </div>

              {/* Barcode */}
              <div className="header-barcode">
                {blend.blend_number && (
                  <Barcode
                    value={blend.blend_number}
                    format="CODE128"
                    width={1.5}
                    height={50}
                    displayValue={false}
                    background="#ffffff"
                    lineColor="#000000"
                    margin={0}
                  />
                )}
              </div>

              <div className="blend-batch-info">
                Batch: {batch.batch_name} | Fragrance: {blend.fragrance_oil_name}
              </div>
            </div>

            {/* Melter Information */}
            <div className="melter-section">
              <h2>Melter Information</h2>
              <div className="melter-grid">
                <div className="melter-item">
                  <span className="melter-label">Selected Melter:</span>
                  <span className="melter-value large">{blend.melters?.name || 'N/A'}</span>
                </div>
                <div className="melter-item">
                  <span className="melter-label">Tank Capacity:</span>
                  <span className="melter-value">{blend.melters?.capacity_lbs || 'N/A'} lbs</span>
                </div>
                <div className="melter-item">
                  <span className="melter-label">Wax Fill Height:</span>
                  <span className="melter-value large highlight">{blend.wax_inches || 'N/A'} inches</span>
                </div>
                <div className="melter-item">
                  <span className="melter-label">Tank Utilization:</span>
                  <span className="melter-value">
                    {blend.melters
                      ? ((parseFloat(blend.total_wax_lbs) / parseFloat(blend.melters.capacity_lbs)) * 100).toFixed(1)
                      : 'N/A'}%
                  </span>
                </div>
              </div>
            </div>

            {/* Materials Section */}
            <div className="materials-section">
              <h2>Materials Required</h2>
              <div className="materials-grid">
                <div className="material-card wax">
                  <div className="material-label">WAX</div>
                  <div className="material-amount">{blend.total_wax_lbs} lbs</div>
                  <div className="material-sublabel">= {blend.wax_inches} inches in {blend.melters?.name}</div>
                </div>
                <div className="material-card oil">
                  <div className="material-label">FRAGRANCE OIL</div>
                  <div className="material-amount">{blend.total_oil_lbs} lbs</div>
                  <div className="material-sublabel">{blend.fragrance_oil_name}</div>
                </div>
                <div className="material-card units">
                  <div className="material-label">TOTAL UNITS</div>
                  <div className="material-amount">{blend.total_units}</div>
                  <div className="material-sublabel">vessels to produce</div>
                </div>
              </div>
            </div>

            {/* Production Details */}
            <div className="production-details">
              <h2>Production Details</h2>
              <div className="details-grid">
                <div className="detail-row">
                  <span className="detail-label">Blend Date:</span>
                  <span className="detail-value">{blendDate}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className="detail-value status">{blend.status.replace('_', ' ').toUpperCase()}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Operator:</span>
                  <span className="detail-value">{blend.operator_name || '_____________________'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Print Date:</span>
                  <span className="detail-value">{currentDate}</span>
                </div>
              </div>

              {blend.notes && (
                <div className="notes-section">
                  <span className="detail-label">Notes:</span>
                  <div className="notes-content">{blend.notes}</div>
                </div>
              )}
            </div>

            {/* Work Orders Breakdown */}
            <div className="work-orders-breakdown">
              <h2>Work Orders Included</h2>
              <table className="wo-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>WO Number</th>
                    <th>Item</th>
                    <th>Quantity</th>
                    <th>Units</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedWorkOrders).map(([customer, orders]) => (
                    <React.Fragment key={customer}>
                      {orders.map((wo, index) => (
                        <tr key={wo.id || index}>
                          <td>{index === 0 ? customer : ''}</td>
                          <td>{wo.work_order_number}</td>
                          <td>{wo.item_description}</td>
                          <td>{wo.blend_quantity}</td>
                          <td>{wo.blend_quantity * (wo.case_qty || 1)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="4" className="total-label">Total:</td>
                    <td className="total-value">{blend.total_units} units</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Sign-off Section */}
            <div className="signoff-section">
              <h2>Production Sign-off</h2>
              <div className="signoff-grid">
                <div className="signoff-item">
                  <div className="signoff-label">Melter Cleaned:</div>
                  <div className="signoff-line"></div>
                  <div className="signoff-sublabel">Initial & Time</div>
                </div>
                <div className="signoff-item">
                  <div className="signoff-label">Materials Added:</div>
                  <div className="signoff-line"></div>
                  <div className="signoff-sublabel">Initial & Time</div>
                </div>
                <div className="signoff-item">
                  <div className="signoff-label">Temperature Check:</div>
                  <div className="signoff-line"></div>
                  <div className="signoff-sublabel">Temp °F & Initial</div>
                </div>
                <div className="signoff-item">
                  <div className="signoff-label">QC Approval:</div>
                  <div className="signoff-line"></div>
                  <div className="signoff-sublabel">Initial & Date</div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="blend-footer">
              <div className="footer-text">
                Generated from Batch {batch.batch_name} on {currentDate}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BlendSheet;